import {
  ChargeStatusSummary,
  getTrafficFines,
  TrafficFineStatusSummaryRow,
  TrafficFinesPartnerScope,
} from "../../../common/services/trafficfines.api";
import { CHARGE_STATUS } from "../../../types/charge.type";
import {
  StatusCounterFinesReport,
  TRAFFICFINE_STATUS,
} from "../../../types/trafficfine.type";

export const EMPTY_TRAFFIC_FINES_STATUS_COUNTER: StatusCounterFinesReport = {
  paidFines: 0,
  openFines: 0,
  expiredFines: 0,
  paidCharges: 0,
  openCharges: 0,
  expiredCharges: 0,
};

export type TrafficFinesDashboardDateRange = {
  startDate: string;
  endDate: string;
};

export type TrafficFinesDashboardScope = TrafficFinesPartnerScope;

export type ManagementTrafficFinesStatusCounter = {
  paidCharges: number;
  openCharges: number;
  expiredCharges: number;
};

export const EMPTY_MANAGEMENT_TRAFFIC_FINES_STATUS_COUNTER: ManagementTrafficFinesStatusCounter =
  {
    paidCharges: 0,
    openCharges: 0,
    expiredCharges: 0,
  };

type TrafficFinesSummarySource = Pick<
  Awaited<ReturnType<typeof getTrafficFines>>,
  "statusCharge" | "statusInfracoes"
>;

function normalizeTrafficFineStatus(
  item: TrafficFineStatusSummaryRow,
): TRAFFICFINE_STATUS | null {
  const rawStatus =
    typeof item.status === "string"
      ? item.status
      : typeof item._id === "string"
        ? item._id
        : item._id?.status;

  if (!rawStatus) {
    return null;
  }

  const normalizedStatus = rawStatus.toLowerCase();

  if (normalizedStatus === TRAFFICFINE_STATUS.PAID) {
    return TRAFFICFINE_STATUS.PAID;
  }

  if (normalizedStatus === TRAFFICFINE_STATUS.OPEN) {
    return TRAFFICFINE_STATUS.OPEN;
  }

  if (normalizedStatus === TRAFFICFINE_STATUS.OVERDUE) {
    return TRAFFICFINE_STATUS.OVERDUE;
  }

  return null;
}

export function buildTrafficFinesStatusCounter(
  source: TrafficFinesSummarySource,
): StatusCounterFinesReport {
  const statusCounter = { ...EMPTY_TRAFFIC_FINES_STATUS_COUNTER };

  if (Array.isArray(source.statusInfracoes)) {
    source.statusInfracoes.forEach((item) => {
      const status = normalizeTrafficFineStatus(item);

      if (status === TRAFFICFINE_STATUS.PAID) {
        statusCounter.paidFines = item.totalValue || 0;
      } else if (status === TRAFFICFINE_STATUS.OPEN) {
        statusCounter.openFines = item.totalValue || 0;
      } else if (status === TRAFFICFINE_STATUS.OVERDUE) {
        statusCounter.expiredFines = item.totalValue || 0;
      }
    });
  }

  if (!source.statusCharge) {
    return statusCounter;
  }

  if (!Array.isArray(source.statusCharge)) {
    Object.entries(source.statusCharge).forEach(([status, data]) => {
      const statusData = data as { count: number; totalValue: number };

      if (status === CHARGE_STATUS.RECEIVED) {
        statusCounter.paidCharges = statusData.totalValue || 0;
      } else if (status === CHARGE_STATUS.PENDING) {
        statusCounter.openCharges = statusData.totalValue || 0;
      } else if (status === CHARGE_STATUS.OVERDUE) {
        statusCounter.expiredCharges = statusData.totalValue || 0;
      }
    });

    return statusCounter;
  }

  source.statusCharge.forEach((item) => {
    if (item.status === CHARGE_STATUS.RECEIVED) {
      statusCounter.paidCharges = item.totalValue || 0;
    } else if (item.status === CHARGE_STATUS.PENDING) {
      statusCounter.openCharges = item.totalValue || 0;
    } else if (item.status === CHARGE_STATUS.OVERDUE) {
      statusCounter.expiredCharges = item.totalValue || 0;
    }
  });

  return statusCounter;
}

export function buildManagementTrafficFinesStatusCounter(
  summary?: ChargeStatusSummary,
): ManagementTrafficFinesStatusCounter {
  return {
    paidCharges: summary?.[CHARGE_STATUS.RECEIVED]?.totalValue || 0,
    openCharges: summary?.[CHARGE_STATUS.PENDING]?.totalValue || 0,
    expiredCharges: summary?.[CHARGE_STATUS.OVERDUE]?.totalValue || 0,
  };
}

export async function getTrafficFinesDashboardSummary(
  dateRange?: Partial<TrafficFinesDashboardDateRange>,
  scope: TrafficFinesDashboardScope = "all",
) {
  const result = await getTrafficFines({
    page: 1,
    itemsPerPage: "1",
    fields: "trafficTicket",
    startDateTrafficFine: dateRange?.startDate ?? "",
    endDateTrafficFine: dateRange?.endDate ?? "",
    partnerScope: scope,
  });

  return {
    ok: result.ok,
    message: result.message,
    statusCounter: buildTrafficFinesStatusCounter(result),
    managementStatusCounter: buildManagementTrafficFinesStatusCounter(
      result.managementStatusCharge,
    ),
  };
}
